
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { ReactNode } from "react";
import { MetalType } from "@/hooks/useMetalSelection";

interface MetalCardWrapperProps {
  metal: MetalType;
  activeMetal: MetalType;
  onSelect: () => void;
  children: ReactNode;
}

export function MetalCardWrapper({ 
  metal, 
  activeMetal, 
  onSelect, 
  children 
}: MetalCardWrapperProps) {
  const isActive = activeMetal === metal;
  
  // Simplified animation variants - reduced complexity to avoid flickering
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring",
        stiffness: 300,
        damping: 24
      } 
    }
  };

  // Hover/tap effects separated from main animation for better control
  const interactionVariants = {
    hover: { scale: 1.03 },
    tap: { scale: 0.98 }
  };

  // Simplified glow effect with reduced transition duration
  const glowVariants = {
    inactive: { 
      boxShadow: "0px 0px 0px rgba(0, 0, 0, 0)" 
    },
    active: { 
      boxShadow: metal === 'gold' 
        ? "0px 0px 15px rgba(255, 215, 0, 0.15)" 
        : "0px 0px 15px rgba(192, 192, 192, 0.15)",
      transition: { 
        duration: 0.3 
      }
    }
  };
  
  return (
    <motion.div 
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      whileTap="tap"
      className={cn(
        "transform cursor-pointer rounded-lg overflow-hidden",
        "transition-colors duration-300"
      )}
      onClick={onSelect}
      // Remove layout animation which can cause flickering
    >
      <motion.div
        variants={glowVariants}
        initial="inactive"
        animate={isActive ? "active" : "inactive"}
        className={cn(
          "h-full w-full rounded-lg",
          isActive 
            ? metal === 'gold'
              ? "bg-gold/5 border border-gold/30" 
              : "bg-silver/5 border border-silver/30"
            : "bg-transparent border border-transparent"
        )}
        style={{
          willChange: "transform, opacity" // Optimize for GPU acceleration
        }}
      >
        {/* Simplified content without AnimatePresence for smoother transitions */}
        <motion.div
          className="h-full"
          initial={{ opacity: 0.9 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          {children}
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
